// Quantum Transport in a project: the desktop's tabs, modes and parameter form, a run button, and the plots.
// Energy Bands / Transport Simulation are here; Device Characterization and T1 Simulation (2D tabs) have their own panels.
import { ActionIcon, Alert, Anchor, Badge, Button, Card, Grid, Group, Loader, Menu, Modal, SimpleGrid, Stack, Switch, Tabs,
  Text, TextInput, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ErrorBox, notifyError, notifyOk } from "../components/Errors";
import { latestRun, limitError, PreviousRuns, RunCard } from "../components/RunCard";
import { TransportPlots } from "../components/TransportPlots";
import { patch, post, type Job, type ProjectDetail } from "../lib/api";
import { useLiveJob, useMe } from "../lib/hooks";
import { formatValue, groupSpecs, refMarker, resolveForm, squareWithin, useTransportForms, type FormMemory, type Raw,
  type Reference, type Resolved, type SavedSet, type TransportSpec } from "../lib/transport";
import { CharacterizePanel } from "./CharacterizePanel";
import { ReadoutPanel } from "./ReadoutPanel";

type Edited = Record<string, Raw>;
const key = (tab: string, mode: string) => `${tab}/${mode}`;

export function TransportWorkspace({ project }: { project: ProjectDetail }) {
  const forms = useTransportForms();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const memory = (project.settings?.transport ?? null) as FormMemory | null;
  const view = (project.settings?.transport_view ?? memory) as { tab: string; mode: string } | null;   // the last run's tab and mode
  const [tab, setTab] = useState<string>(view?.tab ?? "mos2");
  const [mode, setMode] = useState<string>(view?.mode ?? "energy");
  const [edited, setEdited] = useState<Record<string, Edited>>(memory ? { [key(memory.tab, memory.mode)]: memory.edited } : {});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const current = edited[key(tab, mode)] ?? {};
  const [debounced] = useDebouncedValue(current, 300);

  const specs: TransportSpec[] = forms.data?.specs[tab]?.[mode] ?? [];
  const resolved = useQuery({
    queryKey: ["transport-resolve", tab, mode, debounced],
    queryFn: () => resolveForm(tab, mode, debounced),
    enabled: !!forms.data && (mode === "energy" || mode === "transport"),
    placeholderData: (prev) => prev,
  });
  const r: Resolved | undefined = resolved.data;

  // A fresh form whose desktop-default map is larger than the plan allows: shrink it to fit, and say so.
  useEffect(() => {
    const k = key(tab, mode);
    if (!r || edited[k] || !r.limit || !r.params) return;
    const next: Edited = {};
    let note = "";
    if (r.limit.limit === "transport.max_grid_points") {
      const side = String(squareWithin(r.limit.allowed));
      next.vg_step = side;
      next.vsd_step = side;
      note = `The desktop default map (${r.params.vg_step} × ${r.params.vsd_step}) is larger than your plan allows; set to ${side} × ${side}.`;
    } else if (r.limit.limit === "transport.max_field_steps") {
      next.b_step = String(r.limit.allowed);
      note = `Field steps set to ${r.limit.allowed}, the most your plan allows.`;
    }
    if (Object.keys(next).length) {
      setEdited((e) => ({ ...e, [k]: next }));
      setNotes((n) => ({ ...n, [k]: note }));
    }
  }, [r, tab, mode, edited]);

  const setField = useCallback((name: string, value: Raw) => {
    setEdited((e) => ({ ...e, [key(tab, mode)]: { ...(e[key(tab, mode)] ?? {}), [name]: value } }));
  }, [tab, mode]);
  const clearField = (name: string) =>
    setEdited((e) => {
      const cur = { ...(e[key(tab, mode)] ?? {}) };
      delete cur[name];
      return { ...e, [key(tab, mode)]: cur };
    });
  const resetForm = () => {
    setEdited((e) => {
      const next = { ...e };
      delete next[key(tab, mode)];
      return next;
    });
    setNotes((n) => ({ ...n, [key(tab, mode)]: "" }));
  };

  // the job shown on the right: the one just started, else the project's latest Quantum Transport result
  const latest = latestRun(project, "transport.compute");
  const [jobId, setJobId] = useState<string | undefined>(latest?.id);
  useEffect(() => { if (!jobId && latest) setJobId(latest.id); }, [latest, jobId]);
  const { job } = useLiveJob(jobId);

  const settingsSave = useMutation({
    mutationFn: (settings: Record<string, unknown>) => patch(`/projects/${project.id}`, { settings }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", project.id] }),
  });
  const run = useMutation({
    mutationFn: async () => {
      const fresh = await resolveForm(tab, mode, current);           // never run on a stale validation
      if (!fresh.ok || !fresh.inputs) throw new Error("Fix the fields marked in red first.");
      return post<Job>("/jobs", { kind: "transport.compute", project_id: project.id, params: { tab, mode, values: fresh.inputs } });
    },
    onSuccess: (j) => {
      setJobId(j.id);
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["me"] });
      settingsSave.mutate({ ...project.settings, transport: { tab, mode, edited: current }, transport_view: { tab, mode } });
    },
  });

  const tabDef = forms.data?.tabs.find((t) => t.key === tab);
  // a tab without the chosen mode (Si / Ge have no Device Characterization or T1 Simulation): back to Energy Bands
  useEffect(() => { if (tabDef && !tabDef.modes.includes(mode)) setMode("energy"); }, [tabDef, mode]);

  // "Apply fitted values to Energy Bands / Transport" (Device Characterization), as on the desktop
  const applyFit = (values: Record<string, string>) => {
    setEdited((e) => {
      const next = { ...e };
      for (const m of ["energy", "transport"]) {
        const names = new Set((forms.data?.specs[tab]?.[m] ?? []).map((s) => s.name));
        next[key(tab, m)] = { ...(e[key(tab, m)] ?? {}), ...Object.fromEntries(Object.entries(values).filter(([n]) => names.has(n))) };
      }
      return next;
    });
    setMode("energy");
    notifyOk("Fitted values applied to Energy Bands / Transport.");
  };

  if (forms.isLoading || !forms.data) return <Card withBorder><Loader size="sm" /></Card>;
  const header: ReactNode = (
    <>
      <Tabs value={tab} onChange={(v) => v && setTab(v)}>
        <Tabs.List>
          {forms.data.tabs.map((t) => <Tabs.Tab key={t.key} value={t.key}>{t.label}</Tabs.Tab>)}
        </Tabs.List>
      </Tabs>
      <Group justify="space-between" mt="sm" wrap="wrap">
        <Group gap={4} role="radiogroup" aria-label="Calculation mode">
          {forms.data.modes.filter((m) => tabDef?.modes.includes(m.key)).map((m) => {
            const marker = refMarker(tabDef?.references?.[m.key]);
            return (
              <Button key={m.key} size="compact-sm" variant={m.key === mode ? "filled" : "default"} role="radio"
                aria-checked={m.key === mode} onClick={() => setMode(m.key)}>{m.label}{marker && ` ${marker}`}</Button>
            );
          })}
          <ModeReferenceLinks numbers={tabDef?.references?.[mode]} references={forms.data.references} />
        </Group>
        {(mode === "energy" || mode === "transport") && (
          <SavedSets project={project} tab={tab} mode={mode} edited={current}
            onLoad={(s) => { setTab(s.tab); setMode(s.mode); setEdited((e) => ({ ...e, [key(s.tab, s.mode)]: s.edited })); }} />
        )}
      </Group>
      {tabDef?.note && <Text size="sm" c="dimmed" mt="xs">{tabDef.note}</Text>}
    </>
  );
  if (mode === "characterize" && tabDef?.modes.includes(mode))
    return <CharacterizePanel key={tab} project={project} tab={tab} forms={forms.data.device} header={header} onApply={applyFit} />;
  if (mode === "t1sim" && tabDef?.modes.includes(mode))
    return <ReadoutPanel key={tab} project={project} tab={tab} forms={forms.data.device} header={header} />;
  const grid = r?.params && mode === "transport" ? Number(r.params.vg_step) * Number(r.params.vsd_step) : null;
  const maxGrid = Number(me?.limits["transport.max_grid_points"] ?? 0);
  const busy = job && (job.status === "queued" || job.status === "running");

  return (
    <Grid gutter="md">
      <Grid.Col span={{ base: 12, lg: 5 }}>
        <Card withBorder>
          {header}
          {notes[key(tab, mode)] && <Alert mt="xs" color="blue" p="xs"><Text size="xs">{notes[key(tab, mode)]}</Text></Alert>}

          <Stack gap="md" mt="md">
            {groupSpecs(specs).map(([group, list]) => (
              <div key={group}>
                <Text fw={600} size="sm" mb={4}>{group}</Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
                  {list.map((s) => (
                    <Field key={s.name} spec={s} edited={current[s.name]} resolved={r}
                      onChange={(v) => setField(s.name, v)} onReset={() => clearField(s.name)} />
                  ))}
                </SimpleGrid>
              </div>
            ))}
          </Stack>

          {grid != null && (
            <Text size="xs" mt="sm" c={grid > maxGrid ? "orange" : "dimmed"}>
              Map: {grid.toLocaleString()} points (your plan: up to {maxGrid.toLocaleString()}).
              {grid > (forms.data.preview_steps ** 2) ? ` A ${forms.data.preview_steps} × ${forms.data.preview_steps} preview appears first.` : ""}
            </Text>
          )}
          {r?.limit && <ErrorBox error={limitError(r.limit)} />}
          {r && !r.ok && r.errors[""] && <Alert color="red" mt="xs">{r.errors[""]}</Alert>}
          <ErrorBox error={run.error} />
          <Group mt="md" justify="space-between">
            <Button variant="subtle" size="xs" onClick={resetForm}>Reset to defaults</Button>
            <Button onClick={() => run.mutate()} loading={run.isPending} disabled={!r?.ok || !!r?.limit || !!busy}>
              {busy ? "Running…" : "Run"}
            </Button>
          </Group>
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 7 }}>
        <RunCard job={job} exportName={job ? exportName(job) : undefined}
          empty={<Text c="dimmed">Set the parameters and press Run. The plots appear here.</Text>}>
          {job?.result && <TransportPlots result={job.result} />}
        </RunCard>
        <PreviousRuns project={project} kind="transport.compute" current={jobId} onOpen={setJobId}
          describe={(p) => `${(p.tab ?? p.material ?? "").toString().toUpperCase()} · ${p.mode === "transport" ? "Transport" : "Energy bands"}`
            + (p.mode === "transport" && p.values ? ` · ${p.values.vg_step} × ${p.values.vsd_step}` : "")} />
      </Grid.Col>
    </Grid>
  );
}

function exportName(job: Job) {
  const r = job.result ?? {};
  return `transport_${r.tab ?? r.material}_${r.mode}_export.zip`;
}

function Field({ spec, edited, resolved, onChange, onReset }: {
  spec: TransportSpec; edited: Raw | undefined; resolved: Resolved | undefined; onChange: (v: Raw) => void; onReset: () => void;
}) {
  const error = resolved && !resolved.ok ? resolved.errors[spec.name] : undefined;
  const autoValue = resolved?.auto?.[spec.name];
  const isAuto = spec.derived || (spec.auto && edited === undefined);
  const shown: Raw = edited !== undefined ? edited
    : spec.derived ? formatValue(resolved?.params?.[spec.name] ?? spec.default)
    : spec.auto && autoValue !== undefined ? formatValue(autoValue) : formatValue(spec.default);
  const range = spec.min != null || spec.max != null
    ? `${spec.min != null ? spec.min.toLocaleString() : "…"} to ${spec.max != null ? spec.max.toLocaleString() : "…"}${spec.unit ? ` ${spec.unit}` : ""}`
    : "";
  const tip = [spec.help, range && `Allowed: ${range}`, spec.derived ? "Calculated from the other parameters." : "",
    spec.auto && !spec.derived ? "Follows the other parameters until you change it." : ""].filter(Boolean).join(" ");
  const label = (
    <Group gap={4} wrap="nowrap">
      <span>{spec.label}</span>
      {isAuto && <Badge size="xs" variant="light" color="gray">{spec.derived ? "calculated" : "auto"}</Badge>}
    </Group>
  );
  if (spec.kind === "bool") {
    return (
      <Tooltip label={tip} disabled={!tip} multiline w={300} withArrow>
        <Switch mt="lg" label={spec.label} checked={shown === true || shown === "true" || shown === "1"}
          onChange={(e) => onChange(e.currentTarget.checked)} />
      </Tooltip>
    );
  }
  return (
    <Tooltip label={tip} disabled={!tip} multiline w={300} withArrow openDelay={400}>
      <TextInput size="xs" label={label} value={String(shown)} error={error} readOnly={spec.derived}
        inputMode="decimal" aria-label={spec.label}
        rightSectionWidth={spec.unit ? 60 : 30}
        rightSection={
          <Group gap={2} wrap="nowrap" pr={4}>
            {spec.unit && <Text size="xs" c="dimmed">{spec.unit}</Text>}
            {spec.auto && !spec.derived && edited !== undefined && (
              <ActionIcon size="xs" variant="subtle" aria-label={`Back to automatic ${spec.label}`} onClick={onReset}>↺</ActionIcon>
            )}
          </Group>
        }
        onChange={(e) => onChange(e.currentTarget.value)} />
    </Tooltip>
  );
}

function SavedSets({ project, tab, mode, edited, onLoad }: {
  project: ProjectDetail; tab: string; mode: string; edited: Edited; onLoad: (s: SavedSet) => void;
}) {
  const qc = useQueryClient();
  const sets = ((project.settings?.transport_sets as SavedSet[] | undefined) ?? []);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const save = useMutation({
    mutationFn: (next: SavedSet[]) => patch(`/projects/${project.id}`, { settings: { ...project.settings, transport_sets: next } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", project.id] }),
    onError: (e) => notifyError(e),
  });
  const add = () => {
    const entry: SavedSet = { name: name.trim(), tab, mode, edited, saved_at: new Date().toISOString() };
    save.mutate([...sets.filter((s) => s.name !== entry.name), entry], { onSuccess: () => notifyOk(`Saved "${entry.name}".`) });
    setNaming(false);
    setName("");
  };
  return (
    <>
      <Menu position="bottom-end" withinPortal>
        <Menu.Target><Button size="xs" variant="default">Parameter sets</Button></Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => setNaming(true)}>Save current parameters…</Menu.Item>
          {sets.length > 0 && <Menu.Divider />}
          {sets.map((s) => (
            <Menu.Item key={s.name} onClick={() => onLoad(s)}
              rightSection={<ActionIcon size="xs" variant="subtle" color="red" aria-label={`Delete ${s.name}`}
                onClick={(e) => { e.stopPropagation(); save.mutate(sets.filter((x) => x.name !== s.name)); }}>✕</ActionIcon>}>
              {s.name} <Text span size="xs" c="dimmed">({s.tab}, {s.mode})</Text>
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
      <Modal opened={naming} onClose={() => setNaming(false)} title="Save parameter set" centered>
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) add(); }}>
          <Stack>
            <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus maxLength={80} />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setNaming(false)}>Cancel</Button>
              <Button type="submit" disabled={!name.trim()}>Save</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}

/** The selected mode's reference number(s) as links to the papers, next to the mode buttons (as the desktop shows them
 * beside its mode dropdown); the full list is on the References page. */
function ModeReferenceLinks({ numbers, references }: { numbers?: number[]; references?: Reference[] }) {
  if (!numbers?.length) return null;
  const byNumber = new Map((references ?? []).map((r) => [r.number, r]));
  return (
    <Text size="sm" ml={4} title="Open the source paper for this method" data-testid="mode-references">
      [{numbers.map((n, i) => {
        const r = byNumber.get(n);
        return (
          <span key={n}>
            {i > 0 && ","}
            {r?.url ? <Anchor href={r.url} target="_blank" rel="noopener noreferrer" size="sm">{n}</Anchor> : n}
          </span>
        );
      })}]
    </Text>
  );
}
